import { useEffect, useMemo, useRef } from 'react';
import { panCamera, zoomCameraAt } from './camera';
import type { MapSize } from './camera';
import { screenToWorldX, screenToWorldY } from './projection';
import type { Camera, Viewport } from './projection';

/*
 * קלט שדה הקרב: מצלמה, בחירה ופקודות.
 *
 * **לעכבר ולמגע סמנטיקה שונה בכוונה**, כי הם באמת שונים — למגע אין כפתור ימני
 * ואין מצב ריחוף:
 *
 *   עכבר:  גרירה שמאלית = בחירה בקופסה · לחיצה ימנית = פקודה · גלגל/אמצעי = הזזה
 *          גלגלת = זום · WASD/חיצים = הזזה · Shift = הוספה לבחירה
 *   מגע:   אצבע אחת נגררת = הזזת מצלמה · לחיצה ארוכה + גרירה = בחירה בקופסה
 *          הקשה = בחירה, או פקודה אם כבר יש בחירה · שתי אצבעות = זום
 *
 * `touch-action: none` על המכולה אינו קישוט: בלעדיו הגרירה נלחמת בגלילת הדף,
 * ב-rubber-band וב-double-tap zoom של iOS, והמפה נתקעת באמצע תנועה.
 *
 * המצלמה חיה ב-ref ולא ב-state: היא משתנה בכל פריים, ורינדור של React בכל תזוזה
 * היה הופך גרירה חלקה לגרירה מקרטעת.
 */

/** פיקסלים לשנייה בהזזה מהמקלדת. */
export const KEYBOARD_PAN_PX_PER_SEC = 900;

/*
 * שלושת המספרים האלה הם הכשל השקט הכי תכוף שהיה במשחק.
 *
 * הם היו 8 / 320 / 6, וזה מתחת לרעש של אצבע אמיתית: מי שהחזיק את האצבע רבע
 * שנייה כדי לכוון נכנס לבחירה בקופסה, האצבע זזה שישה פיקסלים (פחות ממילימטר
 * וחצי), הקופסה עברה את הסף — **והבחירה שלו הוחלפה בריקה**. הוא חשב שהוא נותן
 * פקודת תנועה, וקיבל צבא בלי בחירה בלי שום הודעה.
 */

/** מעבר לזה, הקשה נחשבת לגרירה. */
const TAP_SLOP_PX = 10;

/** כמה זמן להחזיק אצבע במקום כדי להיכנס לבחירה בקופסה. 450 היא המוסכמה. */
const LONG_PRESS_MS = 450;

/** קופסה קטנה מזה נחשבת להקשה ולא לבחירה. חייב להיות מעל רעש האצבע. */
const MIN_BOX_PX = 14;

/** חלון הזמן והמרחק להקשה כפולה. במגע אין אירוע dblclick, ולכן מזהים ידנית. */
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_SLOP_PX = 28;

export const PAN_KEYS: Readonly<Record<string, readonly [number, number]>> = {
    KeyW: [0, -1],
    ArrowUp: [0, -1],
    KeyS: [0, 1],
    ArrowDown: [0, 1],
    KeyA: [-1, 0],
    ArrowLeft: [-1, 0],
    KeyD: [1, 0],
    ArrowRight: [1, 0],
};

/**
 * תזוזת המצלמה בפיקסלים לפי המקשים הלחוצים ולפי הזמן שעבר.
 *
 * פונקציה טהורה בכוונה: החיבור שלה ללולאת הציור דק, אבל אי אפשר לבדוק אותה בדפדפן —
 * הלולאה נשענת על requestAnimationFrame, שאינו נקרא כשהחלון אינו מצויר.
 *
 * מחזירה את התזוזה **של התוכן**, כלומר בסימן ההפוך לכיוון המקש — בדיוק כמו גרירה.
 */
export function keyboardPanDelta(
    pressed: Iterable<string>,
    deltaMs: number,
): readonly [number, number] {
    let dx = 0;
    let dy = 0;
    for (const code of pressed) {
        const dir = PAN_KEYS[code];
        if (dir) {
            dx += dir[0];
            dy += dir[1];
        }
    }
    if (dx === 0 && dy === 0) return [0, 0];

    const distance = (KEYBOARD_PAN_PX_PER_SEC * deltaMs) / 1000;
    // מנרמלים 0-, שהוא שווה ל-0 בהשוואה רגילה אבל לא תחת Object.is.
    return [dx === 0 ? 0 : -dx * distance, dy === 0 ? 0 : -dy * distance];
}

export interface ScreenBox {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
}

export interface WorldRect {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

export interface BattlefieldInteraction {
    /** נבחרה קופסה. additive = הוספה לבחירה הקיימת. */
    readonly onBoxSelect: (rect: WorldRect, additive: boolean) => void;
    /** הקשה או לחיצה שמאלית. `isDouble` = הקשה כפולה על אותו מקום. */
    readonly onTap: (
        worldX: number,
        worldY: number,
        isTouch: boolean,
        isDouble: boolean,
    ) => void;
    /** פקודת יעד: לחיצה ימנית, או הקשה כשיש בחירה. */
    readonly onCommand: (worldX: number, worldY: number) => void;
    /** קופסת הבחירה שנגררת עכשיו, בקואורדינטות מסך. null = אין. */
    readonly onDragBox: (box: ScreenBox | null) => void;
}

export interface CameraControls {
    /** מיושם מתוך לולאת הציור, כדי שההזזה תהיה רציפה ולא קופצת לפי קצב חזרת המקש. */
    readonly applyKeyboardPan: (deltaMs: number) => void;
}

type Gesture = 'none' | 'pending' | 'pan' | 'box' | 'pinch';

export function useCameraControls(
    containerRef: React.RefObject<HTMLElement | null>,
    cameraRef: React.RefObject<Camera>,
    viewportRef: React.RefObject<Viewport>,
    map: MapSize,
    interaction?: BattlefieldInteraction,
): CameraControls {
    const pressedRef = useRef(new Set<string>());
    const controlsRef = useRef<CameraControls>({ applyKeyboardPan: () => {} });
    const interactionRef = useRef<BattlefieldInteraction | undefined>(interaction);
    interactionRef.current = interaction;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const pointers = new Map<number, { x: number; y: number }>();
        let gesture: Gesture = 'none';
        let pinchDistance = 0;
        let startPoint = { x: 0, y: 0 };
        let box: ScreenBox | null = null;
        let longPressTimer = 0;
        let shiftHeld = false;
        let lastTapTime = 0;
        let lastTapPoint = { x: 0, y: 0 };

        const localPoint = (event: PointerEvent | WheelEvent) => {
            const rect = container.getBoundingClientRect();
            return { x: event.clientX - rect.left, y: event.clientY - rect.top };
        };

        const toWorld = (point: { x: number; y: number }) => {
            const camera = cameraRef.current;
            const viewport = viewportRef.current;
            return {
                x: screenToWorldX(point.x, camera, viewport),
                y: screenToWorldY(point.y, camera, viewport),
            };
        };

        const centerOfPointers = () => {
            let sx = 0;
            let sy = 0;
            for (const p of pointers.values()) {
                sx += p.x;
                sy += p.y;
            }
            return { x: sx / pointers.size, y: sy / pointers.size };
        };

        const distanceOfPointers = () => {
            const [a, b] = [...pointers.values()];
            if (!a || !b) return 0;
            return Math.hypot(a.x - b.x, a.y - b.y);
        };

        const cancelLongPress = () => {
            if (longPressTimer) {
                clearTimeout(longPressTimer);
                longPressTimer = 0;
            }
        };

        const clearBox = () => {
            box = null;
            interactionRef.current?.onDragBox(null);
        };

        /**
         * האם המגע הזה שייך לשדה הקרב, או לכפתור שמרחף מעליו.
         *
         * **זה הבאג שחסם את הבנייה לגמרי.** ה-listeners יושבים על המכולה, וסרגל
         * הבנייה יושב **בתוכה** — ולכן כל הקשה על כפתור בסרגל בועבעה לכאן,
         * ו-`setPointerCapture` על המכולה חטף את הפוינטר מהכפתור. עם פוינטר חטוף
         * אירוע ה-`click` נורה על המכולה ולא על הכפתור, כלומר **ה-onClick של
         * "הצב" פשוט לא רץ**. במגע זה חסם את ההצבה כליל.
         *
         * שדה הקרב הוא הקנבס בלבד. מה שמעליו — סרגל, הודעות, מסך תוצאה — אינו
         * מגע על המפה, וגם לא אמור לשלוח פקודות תנועה בטעות.
         */
        const isBattlefieldTarget = (event: PointerEvent): boolean =>
            event.target instanceof Element && event.target.tagName === 'CANVAS';

        const onPointerDown = (event: PointerEvent) => {
            if (!isBattlefieldTarget(event)) return;
            container.setPointerCapture(event.pointerId);
            const point = localPoint(event);
            pointers.set(event.pointerId, point);
            shiftHeld = event.shiftKey;

            if (pointers.size >= 2) {
                cancelLongPress();
                clearBox();
                gesture = 'pinch';
                pinchDistance = distanceOfPointers();
                return;
            }

            startPoint = point;

            if (event.pointerType === 'mouse') {
                if (event.button === 2) {
                    const world = toWorld(point);
                    interactionRef.current?.onCommand(world.x, world.y);
                    gesture = 'none';
                    return;
                }
                gesture = event.button === 1 ? 'pan' : 'box';
                if (gesture === 'box') {
                    box = { x0: point.x, y0: point.y, x1: point.x, y1: point.y };
                }
                return;
            }

            // מגע: עדיין לא יודעים אם זו הקשה, הזזה או בחירה ארוכה.
            gesture = 'pending';
            longPressTimer = window.setTimeout(() => {
                if (gesture !== 'pending') return;
                gesture = 'box';
                box = { x0: startPoint.x, y0: startPoint.y, x1: startPoint.x, y1: startPoint.y };
                interactionRef.current?.onDragBox(box);
            }, LONG_PRESS_MS);
        };

        const onPointerMove = (event: PointerEvent) => {
            const previous = pointers.get(event.pointerId);
            if (!previous) return;

            const point = localPoint(event);
            const camera = cameraRef.current;
            const viewport = viewportRef.current;

            if (gesture === 'pinch' && pointers.size >= 2) {
                const centerBefore = centerOfPointers();
                pointers.set(event.pointerId, point);
                const centerAfter = centerOfPointers();
                const distance = distanceOfPointers();

                let next = panCamera(
                    camera,
                    centerAfter.x - centerBefore.x,
                    centerAfter.y - centerBefore.y,
                    viewport,
                    map,
                );
                if (pinchDistance > 0 && distance > 0) {
                    next = zoomCameraAt(
                        next,
                        distance / pinchDistance,
                        centerAfter.x,
                        centerAfter.y,
                        viewport,
                        map,
                    );
                }
                pinchDistance = distance;
                cameraRef.current = next;
                return;
            }

            pointers.set(event.pointerId, point);

            if (gesture === 'pending') {
                const moved = Math.hypot(point.x - startPoint.x, point.y - startPoint.y);
                if (moved > TAP_SLOP_PX) {
                    cancelLongPress();
                    gesture = 'pan';
                }
                return;
            }

            if (gesture === 'pan') {
                cameraRef.current = panCamera(
                    camera,
                    point.x - previous.x,
                    point.y - previous.y,
                    viewport,
                    map,
                );
                return;
            }

            if (gesture === 'box' && box) {
                box = { ...box, x1: point.x, y1: point.y };
                interactionRef.current?.onDragBox(box);
            }
        };

        const endPointer = (event: PointerEvent) => {
            // פוינטר שלא התחיל על הקנבס אינו שלנו — הסיום שלו גם לא.
            if (!pointers.has(event.pointerId)) return;
            const point = pointers.get(event.pointerId) ?? startPoint;
            pointers.delete(event.pointerId);
            cancelLongPress();

            const isTouch = event.pointerType !== 'mouse';
            const handlers = interactionRef.current;

            if (gesture === 'box' && box) {
                const width = Math.abs(box.x1 - box.x0);
                const height = Math.abs(box.y1 - box.y0);
                if (width < MIN_BOX_PX && height < MIN_BOX_PX) {
                    // קופסה זעירה היא בעצם הקשה — לא מרוקנים בחירה בטעות.
                    handleTap(point, isTouch, handlers);
                } else {
                    const a = toWorld({ x: box.x0, y: box.y0 });
                    const b = toWorld({ x: box.x1, y: box.y1 });
                    handlers?.onBoxSelect(
                        {
                            minX: Math.min(a.x, b.x),
                            minY: Math.min(a.y, b.y),
                            maxX: Math.max(a.x, b.x),
                            maxY: Math.max(a.y, b.y),
                        },
                        shiftHeld,
                    );
                }
                clearBox();
            } else if (gesture === 'pending') {
                handleTap(point, isTouch, handlers);
            }

            if (pointers.size === 0) {
                gesture = 'none';
                pinchDistance = 0;
            } else if (pointers.size === 1) {
                // אצבע אחת עזבה — המרחק הישן כבר לא רלוונטי, ובלי איפוס הזום קופץ.
                gesture = 'pan';
                pinchDistance = 0;
            }
        };

        /*
         * ההקשה מועברת כמו שהיא, וההחלטה מה היא אומרת נופלת ב-Battlefield.
         *
         * בגרסה הראשונה ההחלטה ישבה כאן: "במגע, הקשה כשיש בחירה היא פקודה". התוצאה
         * הייתה שבטלפון אי אפשר היה להחליף בחירה בכלל — הקשה על יחידה אחרת שלך שלחה
         * את הכוח לזוז לשם. מי שיודע אם מתחת לאצבע יש יחידה שלך הוא מסך הקרב, לא
         * שכבת הקלט.
         */
        const handleTap = (
            point: { x: number; y: number },
            isTouch: boolean,
            handlers: BattlefieldInteraction | undefined,
        ) => {
            if (!handlers) return;

            // הקשה כפולה מזוהה כאן ולא נשענת על אירוע dblclick, שאינו קיים במגע.
            const now = performance.now();
            const isDouble =
                now - lastTapTime < DOUBLE_TAP_MS &&
                Math.hypot(point.x - lastTapPoint.x, point.y - lastTapPoint.y) < DOUBLE_TAP_SLOP_PX;
            lastTapTime = isDouble ? 0 : now;
            lastTapPoint = point;

            const world = toWorld(point);
            handlers.onTap(world.x, world.y, isTouch, isDouble);
        };

        const onContextMenu = (event: Event) => {
            // בלי זה תפריט ההקשר של הדפדפן קופץ על כל פקודה.
            event.preventDefault();
        };

        const onWheel = (event: WheelEvent) => {
            // בלי preventDefault הדפדפן מזים את הדף כולו כשמגלגלים מעל הקנבס.
            event.preventDefault();
            const point = localPoint(event);
            const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
            cameraRef.current = zoomCameraAt(
                cameraRef.current,
                factor,
                point.x,
                point.y,
                viewportRef.current,
                map,
            );
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.code in PAN_KEYS) {
                pressedRef.current.add(event.code);
                event.preventDefault();
                return;
            }
            const camera = cameraRef.current;
            const viewport = viewportRef.current;
            if (event.key === '+' || event.key === '=') {
                cameraRef.current = zoomCameraAt(
                    camera,
                    1.25,
                    viewport.width / 2,
                    viewport.height / 2,
                    viewport,
                    map,
                );
            } else if (event.key === '-' || event.key === '_') {
                cameraRef.current = zoomCameraAt(
                    camera,
                    1 / 1.25,
                    viewport.width / 2,
                    viewport.height / 2,
                    viewport,
                    map,
                );
            }
        };

        const onKeyUp = (event: KeyboardEvent) => {
            pressedRef.current.delete(event.code);
        };

        // מקש שנשאר "לחוץ" כשהחלון מאבד פוקוס גורם למפה להחליק לנצח.
        const onBlur = () => {
            pressedRef.current.clear();
            cancelLongPress();
            clearBox();
            pointers.clear();
            gesture = 'none';
        };

        container.addEventListener('pointerdown', onPointerDown);
        container.addEventListener('pointermove', onPointerMove);
        container.addEventListener('pointerup', endPointer);
        container.addEventListener('pointercancel', endPointer);
        container.addEventListener('contextmenu', onContextMenu);
        container.addEventListener('wheel', onWheel, { passive: false });
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);

        controlsRef.current = {
            applyKeyboardPan: (deltaMs: number) => {
                if (pressedRef.current.size === 0) return;
                const [dx, dy] = keyboardPanDelta(pressedRef.current, deltaMs);
                if (dx === 0 && dy === 0) return;
                cameraRef.current = panCamera(
                    cameraRef.current,
                    dx,
                    dy,
                    viewportRef.current,
                    map,
                );
            },
        };

        return () => {
            container.removeEventListener('pointerdown', onPointerDown);
            container.removeEventListener('pointermove', onPointerMove);
            container.removeEventListener('pointerup', endPointer);
            container.removeEventListener('pointercancel', endPointer);
            container.removeEventListener('contextmenu', onContextMenu);
            container.removeEventListener('wheel', onWheel);
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('blur', onBlur);
            cancelLongPress();
            pressedRef.current.clear();
        };
    }, [containerRef, cameraRef, viewportRef, map]);

    /*
     * העטיפה חייבת להיות יציבה בין רינדורים: היא נכנסת כתלות של לולאת הציור
     * ב-Battlefield, ואובייקט חדש בכל רינדור היה מאתחל את לולאת ה-rAF מחדש.
     */
    return useMemo<CameraControls>(
        () => ({
            applyKeyboardPan: (deltaMs: number) => controlsRef.current.applyKeyboardPan(deltaMs),
        }),
        [],
    );
}
